import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import {
  overlayMessageSchema,
  type OverlayMessage,
  type ParentMessage,
} from "@clarity/shared";

type Props = {
  projectId: string;
  // Path within the project's target origin. Empty / undefined → the proxy
  // redirects to the project's configured target_url path. When this prop
  // changes the iframe's `src` attribute updates in place — React doesn't
  // remount the iframe, the browser just navigates it.
  path?: string;
  onMessage: (msg: OverlayMessage) => void;
};

export type ProxyFrameHandle = {
  send: (message: ParentMessage) => void;
  // Reload the iframe in place (preserves the current path and scroll
  // position is reset by the browser). Same-origin reload — works because
  // the iframe is on clarity.avista.is/proxy/... same as the parent.
  reload: () => void;
};

// Hosts the proxied target inside an iframe and bridges postMessage in both
// directions. The parent component uses the ref handle to push state
// updates (render-comments, scroll-to-comment) down into the overlay, and
// listens via the onMessage callback for click / ready / form-blocked
// signals coming back up.
export const ProxyFrame = forwardRef<ProxyFrameHandle, Props>(function ProxyFrame(
  { projectId, path, onMessage },
  ref,
) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const src = `/proxy/${encodeURIComponent(projectId)}${path && path !== "/" ? path : "/"}`;

  useImperativeHandle(
    ref,
    () => ({
      send(message) {
        const win = iframeRef.current?.contentWindow;
        if (!win) return;
        win.postMessage({ source: "clarity-parent", ...message }, "*");
      },
      reload() {
        const el = iframeRef.current;
        if (!el) return;
        // contentWindow.location.reload() is the clean path (preserves
        // history). Falls back to re-assigning src for the rare case
        // where the contentWindow is inaccessible (e.g. mid-navigation
        // to an opaque cross-origin URL — shouldn't happen for us but
        // costs nothing to guard).
        try {
          el.contentWindow?.location.reload();
        } catch {
          // Re-assigning to the same src triggers a fresh fetch.
          const cur = el.src;
          el.src = "about:blank";
          // Next tick so the browser actually unloads first.
          requestAnimationFrame(() => {
            el.src = cur;
          });
        }
      },
    }),
    [],
  );

  useEffect(() => {
    function onWindowMessage(event: MessageEvent) {
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if ((data as { source?: unknown }).source !== "clarity-overlay") return;
      const parsed = overlayMessageSchema.safeParse(data);
      if (!parsed.success) return;
      onMessage(parsed.data);
    }
    window.addEventListener("message", onWindowMessage);
    return () => window.removeEventListener("message", onWindowMessage);
  }, [onMessage]);

  return (
    <iframe
      ref={iframeRef}
      src={src}
      title="Review surface"
      data-tour-id="iframe"
      className="h-full w-full border-0 bg-white"
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
    />
  );
});
